<template>
  <div class="lg:max-w-7xl lg:mx-auto lg:py-4">
    <!-- Header -->
    <div class="mx-4 mb-4 flex items-start justify-between gap-4">
      <div class="flex items-center gap-3">
        <div
          class="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-600 to-purple-500 flex items-center justify-center flex-shrink-0"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="white"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path
              d="M12 2a8 8 0 0 0-8 8c0 3.5 2.1 6.4 5 7.7V22l3-3 3 3v-4.3c2.9-1.3 5-4.2 5-7.7a8 8 0 0 0-8-8z"
            />
            <circle cx="10" cy="10" r="1" fill="white" />
            <circle cx="14" cy="10" r="1" fill="white" />
          </svg>
        </div>
        <div>
          <h1 class="text-xl font-bold text-gray-900">
            {{ $t("ai.dashboard.title") }}
          </h1>
          <p
            class="text-xs text-gray-400 mt-0.5"
            v-if="meta?.last_generated_at"
          >
            {{ $t("ai.dashboard.lastGenerated") }}:
            {{ formatTime(meta.last_generated_at) }}
          </p>
        </div>
        <span
          v-if="meta && meta.unread_count > 0"
          class="inline-flex items-center rounded-full bg-teal-50 border border-teal-200 px-2.5 py-0.5 text-xs font-semibold text-teal-700"
        >
          {{ meta.unread_count }} {{ $t("ai.dashboard.unread") }}
        </span>
      </div>

      <div class="flex items-center gap-2 flex-shrink-0">
        <button
          v-if="meta && meta.unread_count > 0"
          @click="markAllRead"
          class="inline-flex items-center px-3 py-2 border border-gray-200 rounded-lg shadow-sm text-xs font-medium text-gray-600 bg-white hover:bg-gray-50 hover:border-gray-300 transition-colors focus:outline-none"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="mr-1.5"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
          {{ $t("ai.dashboard.markAllRead") }}
        </button>

        <button
          @click="refresh"
          :disabled="refreshing"
          class="inline-flex items-center px-4 py-2 border border-gray-200 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-300 transition-colors focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <svg
            v-if="refreshing"
            xmlns="http://www.w3.org/2000/svg"
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
            class="animate-spin mr-1.5"
          >
            <path d="M21 12a9 9 0 1 1-6.219-8.56" />
          </svg>
          <svg
            v-else
            xmlns="http://www.w3.org/2000/svg"
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="mr-1.5"
          >
            <polyline points="23 4 23 10 17 10" />
            <path d="M20.49 15a9 9 0 1 1-.53-4.81" />
          </svg>
          {{
            refreshing
              ? $t("ai.dashboard.refreshing")
              : $t("ai.dashboard.refresh")
          }}
        </button>
      </div>
    </div>

    <!-- Meta stats row -->
    <div v-if="meta && aiActivated" class="mx-4 mb-4 grid grid-cols-3 gap-3">
      <div
        class="bg-white rounded-lg shadow-sm border border-gray-100 border-l-4 border-l-teal-400 px-4 py-3 flex items-center gap-3"
      >
        <div
          class="w-8 h-8 rounded-lg bg-teal-50 flex items-center justify-center flex-shrink-0"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#0d9488"
            stroke-width="2"
          >
            <rect x="3" y="3" width="7" height="7" />
            <rect x="14" y="3" width="7" height="7" />
            <rect x="14" y="14" width="7" height="7" />
            <rect x="3" y="14" width="7" height="7" />
          </svg>
        </div>
        <div>
          <p class="text-lg font-bold text-gray-800 leading-none">
            {{ meta.total_panels }}
          </p>
          <p class="text-xs text-gray-400 mt-0.5">
            {{ $t("ai.dashboard.totalPanels") }}
          </p>
        </div>
      </div>

      <div
        class="bg-white rounded-lg shadow-sm border border-gray-100 px-4 py-3 flex items-center gap-3"
        :class="
          meta.active_alerts_count > 0 ? 'border-l-4 border-l-red-400' : ''
        "
      >
        <div
          class="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
          :class="meta.active_alerts_count > 0 ? 'bg-red-50' : 'bg-gray-50'"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            :stroke="meta.active_alerts_count > 0 ? '#ef4444' : '#9ca3af'"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
            <path d="M13.73 21a2 2 0 0 1-3.46 0" />
          </svg>
        </div>
        <div>
          <p
            class="text-lg font-bold leading-none"
            :class="
              meta.active_alerts_count > 0 ? 'text-red-600' : 'text-gray-800'
            "
          >
            {{ meta.active_alerts_count }}
          </p>
          <p class="text-xs text-gray-400 mt-0.5">
            {{ $t("ai.dashboard.activeAlerts") }}
          </p>
        </div>
      </div>

      <div
        class="bg-white rounded-lg shadow-sm border border-gray-100 border-l-4 border-l-purple-400 px-4 py-3 flex items-center gap-3"
      >
        <div
          class="w-8 h-8 rounded-lg bg-purple-50 flex items-center justify-center flex-shrink-0"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#9333ea"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path
              d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"
            />
          </svg>
        </div>
        <div>
          <p class="text-lg font-bold text-gray-800 leading-none">
            {{ meta.total_conversations }}
          </p>
          <p class="text-xs text-gray-400 mt-0.5">
            {{ $t("ai.dashboard.conversations") }}
          </p>
        </div>
      </div>
    </div>

    <!-- AI not activated -->
    <div
      v-if="!aiActivated && !loading"
      class="mx-4 bg-white rounded-lg shadow-sm border border-gray-100 py-16 text-center"
    >
      <div
        class="w-14 h-14 rounded-full bg-gray-50 flex items-center justify-center mx-auto mb-4"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#9ca3af"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path
            d="M12 2a8 8 0 0 0-8 8c0 3.5 2.1 6.4 5 7.7V22l3-3 3 3v-4.3c2.9-1.3 5-4.2 5-7.7a8 8 0 0 0-8-8z"
          />
        </svg>
      </div>
      <h3 class="text-base font-semibold text-gray-700 mb-1">
        {{ $t("ai.dashboard.aiNotActivated") }}
      </h3>
      <p class="text-sm text-gray-400 max-w-sm mx-auto">
        {{ $t("ai.dashboard.aiNotActivatedDesc") }}
      </p>
    </div>

    <!-- Loading skeleton -->
    <div
      v-else-if="loading"
      class="mx-4 flex justify-center items-center gap-1.5 py-20"
    >
      <span
        class="w-2.5 h-2.5 bg-teal-400 rounded-full animate-bounce"
        style="animation-delay: 0ms"
      ></span>
      <span
        class="w-2.5 h-2.5 bg-teal-400 rounded-full animate-bounce"
        style="animation-delay: 150ms"
      ></span>
      <span
        class="w-2.5 h-2.5 bg-teal-400 rounded-full animate-bounce"
        style="animation-delay: 300ms"
      ></span>
    </div>

    <!-- Empty state -->
    <div
      v-else-if="aiActivated && !panels.length"
      class="mx-4 bg-white rounded-lg shadow-sm border border-gray-100 py-16 text-center"
    >
      <div
        class="w-14 h-14 rounded-full bg-teal-50 flex items-center justify-center mx-auto mb-4"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#0d9488"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
        </svg>
      </div>
      <h3 class="text-base font-semibold text-gray-700 mb-1">
        {{ $t("ai.dashboard.noInsights") }}
      </h3>
      <p class="text-sm text-gray-400 max-w-sm mx-auto mb-6">
        {{ $t("ai.dashboard.noInsightsDesc") }}
      </p>
      <button
        @click="refresh"
        :disabled="refreshing"
        class="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium text-white bg-gradient-to-r from-teal-600 to-purple-500 shadow-sm hover:shadow-md hover:opacity-90 transition-all focus:outline-none disabled:opacity-50"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
        </svg>
        {{ $t("ai.dashboard.generateInsights") }}
      </button>
    </div>

    <!-- Panels grid -->
    <div
      v-else-if="aiActivated && panels.length"
      class="mx-4 grid grid-cols-1 lg:grid-cols-3 gap-4"
    >
      <div
        v-for="(panel, panelIndex) in panels"
        :key="panel.id"
        draggable="true"
        @dragstart="onDragStart(panelIndex)"
        @dragover.prevent="onDragOver(panelIndex)"
        @drop.prevent="onDrop(panelIndex)"
        @dragend="onDragEnd"
        :class="[
          panelColSpan(panel),
          severityBorderClass(panel.severity),
          panel.is_read ? '' : 'bg-gradient-to-br from-white to-teal-50/30',
          draggedIndex === panelIndex ? 'opacity-40' : '',
          dragOverIndex === panelIndex && draggedIndex !== panelIndex
            ? 'ring-2 ring-teal-400 ring-offset-1'
            : '',
        ]"
        class="group bg-white rounded-xl shadow-sm border overflow-hidden flex flex-col transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5"
      >
        <!-- Panel header -->
        <div class="px-4 pt-4 pb-3 flex items-start gap-3">
          <!-- Drag handle -->
          <div
            class="flex-shrink-0 mt-1.5 cursor-grab active:cursor-grabbing text-gray-200 group-hover:text-gray-400 transition-colors"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <circle cx="9" cy="5" r="1.8" />
              <circle cx="15" cy="5" r="1.8" />
              <circle cx="9" cy="12" r="1.8" />
              <circle cx="15" cy="12" r="1.8" />
              <circle cx="9" cy="19" r="1.8" />
              <circle cx="15" cy="19" r="1.8" />
            </svg>
          </div>
          <!-- Type icon -->
          <div
            :class="typeIconBgClass(panel.type)"
            class="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
          >
            <!-- alert_summary -->
            <svg
              v-if="panel.type === 'alert_summary'"
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
              <path d="M13.73 21a2 2 0 0 1-3.46 0" />
            </svg>
            <!-- trend / kpi_snapshot -->
            <svg
              v-else-if="
                panel.type === 'trend' || panel.type === 'kpi_snapshot'
              "
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
            </svg>
            <!-- pattern -->
            <svg
              v-else-if="panel.type === 'pattern'"
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
            <!-- recommendation -->
            <svg
              v-else-if="panel.type === 'recommendation'"
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <!-- suggestion -->
            <svg
              v-else-if="panel.type === 'suggestion'"
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
            <!-- quick_action -->
            <svg
              v-else
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
            </svg>
          </div>

          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2 flex-wrap">
              <span
                :class="typeBadgeClass(panel.type)"
                class="text-xs font-semibold px-2 py-0.5 rounded-full"
              >
                {{ $t("ai.dashboard.type." + panel.type) }}
              </span>
              <span
                v-if="panel.severity"
                :class="severityBadgeClass(panel.severity)"
                class="text-xs font-semibold px-2 py-0.5 rounded-full"
              >
                {{ $t("ai.dashboard.severity." + panel.severity) }}
              </span>
              <span
                v-if="!panel.is_read"
                class="w-2 h-2 rounded-full bg-teal-500 flex-shrink-0 animate-pulse"
                :title="$t('ai.dashboard.unread')"
              ></span>
            </div>
            <h3
              class="text-sm font-semibold text-gray-800 leading-tight mt-1.5"
            >
              {{ panel.title }}
            </h3>
          </div>

          <!-- Dismiss button -->
          <button
            @click="dismiss(panel)"
            class="flex-shrink-0 w-6 h-6 rounded-md flex items-center justify-center text-gray-300 hover:text-gray-500 hover:bg-gray-100 transition-colors"
            :title="$t('ai.dashboard.dismiss')"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="11"
              height="11"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <!-- Description -->
        <div
          v-if="panel.description"
          class="px-4 pb-3 text-sm text-gray-500 leading-relaxed"
        >
          {{ panel.description }}
        </div>

        <!-- ── Data: trend / kpi_snapshot metrics ── -->
        <div
          v-if="
            (panel.type === 'trend' || panel.type === 'kpi_snapshot') &&
            panel.data?.metrics?.length
          "
          class="px-4 pb-3 grid grid-cols-2 gap-3"
        >
          <div
            v-for="(metric, idx) in panel.data.metrics"
            :key="idx"
            class="bg-gray-50 border border-gray-100 rounded-lg px-3 py-2.5"
          >
            <p class="text-xs text-gray-400 mb-0.5">{{ metric.label }}</p>
            <p class="text-xl font-bold text-gray-800 leading-none">
              {{ metric.value
              }}<span class="text-xs font-normal text-gray-400 ml-1">{{
                metric.unit
              }}</span>
            </p>
            <span
              v-if="
                metric.change_direction && metric.change_percent !== undefined
              "
              :class="
                metric.change_direction === 'up'
                  ? 'text-green-600 bg-green-50'
                  : metric.change_direction === 'down'
                  ? 'text-red-500 bg-red-50'
                  : 'text-gray-500 bg-gray-100'
              "
              class="inline-flex items-center gap-0.5 text-xs font-semibold px-1.5 py-0.5 rounded-full mt-1"
            >
              <svg
                v-if="metric.change_direction === 'up'"
                xmlns="http://www.w3.org/2000/svg"
                width="9"
                height="9"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="3"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <polyline points="18 15 12 9 6 15" />
              </svg>
              <svg
                v-else-if="metric.change_direction === 'down'"
                xmlns="http://www.w3.org/2000/svg"
                width="9"
                height="9"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="3"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
              <span v-else class="text-[9px]">—</span>
              {{ Math.abs(metric.change_percent) }}%
            </span>
          </div>
        </div>

        <!-- ── Visualization: comparison_bar ── -->
        <div
          v-if="
            panel.visualization?.type === 'comparison_bar' &&
            panel.visualization.series?.length
          "
          class="px-4 pb-3"
        >
          <div class="space-y-2">
            <div
              v-for="(serie, idx) in panel.visualization.series"
              :key="idx"
              class="flex items-center gap-2"
            >
              <span
                class="text-xs text-gray-500 w-20 flex-shrink-0 text-right"
                >{{ serie.label }}</span
              >
              <div class="flex-1 bg-gray-100 rounded-full h-2 overflow-hidden">
                <div
                  :class="idx === 1 ? 'bg-teal-500' : 'bg-gray-300'"
                  class="h-full rounded-full transition-all"
                  :style="`width: ${barWidth(
                    serie.value,
                    panel.visualization.series,
                  )}%`"
                ></div>
              </div>
              <span
                class="text-xs font-semibold text-gray-700 w-10 flex-shrink-0"
                >{{ serie.value }}</span
              >
            </div>
          </div>
        </div>

        <!-- ── Data: pattern ── -->
        <div v-if="panel.type === 'pattern' && panel.data" class="px-4 pb-3">
          <div
            v-if="
              panel.data.key_values && Object.keys(panel.data.key_values).length
            "
            class="flex flex-wrap gap-2 mt-1"
          >
            <span
              v-for="(val, key) in panel.data.key_values"
              :key="key"
              class="inline-flex items-center gap-1 bg-orange-50 border border-orange-100 text-orange-700 rounded-lg px-2.5 py-1 text-xs font-medium"
            >
              <span class="text-orange-400 font-normal"
                >{{ key.replace(/_/g, " ") }}:</span
              >
              {{ val }}
            </span>
          </div>
        </div>

        <!-- ── Data: recommendation ── -->
        <div
          v-if="
            panel.type === 'recommendation' && panel.data?.action_items?.length
          "
          class="px-4 pb-3 space-y-2"
        >
          <div
            v-for="(item, idx) in panel.data.action_items"
            :key="idx"
            class="flex gap-2.5 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2"
          >
            <span
              class="w-5 h-5 rounded-full bg-blue-100 text-blue-600 text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5"
            >
              {{ item.priority || idx + 1 }}
            </span>
            <div>
              <p class="text-xs font-semibold text-blue-800">
                {{ item.action }}
              </p>
              <p v-if="item.rationale" class="text-xs text-blue-600 mt-0.5">
                {{ item.rationale }}
              </p>
            </div>
          </div>
        </div>

        <!-- ── Data: suggestion ── -->
        <div v-if="panel.type === 'suggestion' && panel.data" class="px-4 pb-3">
          <div
            v-if="panel.data.entity_name || panel.data.entity_dn"
            class="text-xs text-gray-400 mb-2"
          >
            {{
              panel.data.entity_type === "queue"
                ? $t("ai.dashboard.queue")
                : $t("ai.dashboard.agent")
            }}:
            <span class="font-semibold text-gray-600">
              {{ panel.data.entity_name || panel.data.entity_dn }}
            </span>
            <span v-if="panel.data.entity_dn" class="text-gray-400 ml-1"
              >({{ panel.data.entity_dn }})</span
            >
          </div>
          <div v-if="panel.data.metrics?.length" class="grid grid-cols-3 gap-2">
            <div
              v-for="(metric, idx) in panel.data.metrics"
              :key="idx"
              class="bg-gray-50 rounded-lg px-2.5 py-2 text-center"
            >
              <p class="text-base font-bold text-gray-800 leading-none">
                {{ metric.value
                }}<span class="text-xs font-normal text-gray-400 ml-0.5">{{
                  metric.unit
                }}</span>
              </p>
              <p class="text-xs text-gray-400 mt-0.5 leading-tight">
                {{ metric.label }}
              </p>
            </div>
          </div>
        </div>

        <!-- ── Data: alert_summary ── -->
        <div
          v-if="panel.type === 'alert_summary' && panel.data"
          class="px-4 pb-3"
        >
          <!-- severity counters -->
          <div class="flex gap-3 mb-3">
            <span
              v-if="panel.data.critical > 0"
              class="inline-flex items-center gap-1 bg-red-50 border border-red-200 text-red-700 rounded-full px-2.5 py-0.5 text-xs font-semibold"
            >
              <span class="w-1.5 h-1.5 rounded-full bg-red-500"></span>
              {{ panel.data.critical }} {{ $t("ai.dashboard.critical") }}
            </span>
            <span
              v-if="panel.data.warning > 0"
              class="inline-flex items-center gap-1 bg-yellow-50 border border-yellow-200 text-yellow-700 rounded-full px-2.5 py-0.5 text-xs font-semibold"
            >
              <span class="w-1.5 h-1.5 rounded-full bg-yellow-400"></span>
              {{ panel.data.warning }} {{ $t("ai.dashboard.warning") }}
            </span>
            <span
              v-if="panel.data.info > 0"
              class="inline-flex items-center gap-1 bg-blue-50 border border-blue-200 text-blue-700 rounded-full px-2.5 py-0.5 text-xs font-semibold"
            >
              <span class="w-1.5 h-1.5 rounded-full bg-blue-400"></span>
              {{ panel.data.info }} {{ $t("ai.dashboard.info") }}
            </span>
          </div>
          <!-- top 3 alerts preview -->
          <div v-if="panel.data.alerts?.length" class="space-y-2">
            <div
              v-for="alert in panel.data.alerts.slice(0, 3)"
              :key="alert.id"
              :class="severityCardClass(alert.severity)"
              class="flex items-start gap-2 rounded-lg border px-3 py-2"
            >
              <div
                :class="severityDotClass(alert.severity)"
                class="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0"
              ></div>
              <div class="min-w-0 flex-1">
                <p class="text-xs font-semibold text-gray-800 truncate">
                  {{ alert.title }}
                </p>
                <p
                  v-if="alert.affected_name || alert.affected_dn"
                  class="text-xs text-gray-400"
                >
                  {{ alert.affected_name || alert.affected_dn }}
                </p>
              </div>
              <span
                v-if="
                  alert.current_value !== null &&
                  alert.current_value !== undefined
                "
                :class="severityValueClass(alert.severity)"
                class="text-xs font-bold flex-shrink-0"
              >
                {{ alert.current_value
                }}{{ alert.metric?.includes("rate") ? "%" : "" }}
              </span>
            </div>
            <button
              @click="$emit('navigate', { tab: 'alerts' })"
              class="w-full text-xs text-teal-600 hover:text-teal-700 font-medium text-center pt-2 pb-0.5 hover:underline cursor-pointer"
            >
              <template v-if="panel.data.alerts.length > 3">
                +{{ panel.data.alerts.length - 3 }}
                {{ $t("ai.dashboard.moreAlerts") }} —
              </template>
              {{ $t("ai.dashboard.viewAllAlerts") }}
            </button>
          </div>
        </div>

        <!-- ── Data: quick_action shortcuts ── -->
        <div
          v-if="panel.type === 'quick_action' && panel.data?.shortcuts?.length"
          class="px-4 pb-3 grid grid-cols-2 sm:grid-cols-3 gap-2"
        >
          <button
            v-for="(shortcut, idx) in panel.data.shortcuts"
            :key="idx"
            @click="$emit('chat-query', shortcut.query)"
            class="flex items-center gap-2 px-3 py-2.5 rounded-lg border border-gray-200 bg-gray-50 hover:bg-teal-50 hover:border-teal-200 text-left transition-colors group cursor-pointer"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="text-gray-400 group-hover:text-teal-600 flex-shrink-0"
            >
              <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
            </svg>
            <span
              class="text-xs font-medium text-gray-600 group-hover:text-teal-700 leading-tight"
              >{{ shortcut.label }}</span
            >
          </button>
        </div>

        <!-- ── Actions: navigate buttons (inline) ── -->
        <div
          v-if="panel.actions?.some((a) => a.type !== 'chat_query')"
          class="mt-auto px-4 pb-4 pt-1 flex flex-wrap gap-2"
        >
          <button
            v-for="(action, idx) in panel.actions.filter(
              (a) => a.type !== 'chat_query',
            )"
            :key="idx"
            @click="handleAction(action, panel)"
            class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-gray-200 text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-300 transition-all duration-150 focus:outline-none"
          >
            <svg
              v-if="action.type === 'navigate'"
              xmlns="http://www.w3.org/2000/svg"
              width="11"
              height="11"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
            {{ action.label }}
          </button>
        </div>

        <!-- Panel footer: Ask AI (left) + timestamp (right) -->
        <div
          class="mt-auto px-4 py-2 border-t border-gray-100 flex items-center justify-between bg-gray-50/50"
        >
          <button
            v-if="panel.actions?.some((a) => a.type === 'chat_query')"
            @click="
              handleAction(
                panel.actions.find((a) => a.type === 'chat_query'),
                panel,
              )
            "
            class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-gradient-to-r from-teal-600 to-purple-500 text-white shadow-sm hover:shadow-md hover:opacity-90 transition-all duration-150 focus:outline-none"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="11"
              height="11"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path
                d="M12 2a8 8 0 0 0-8 8c0 3.5 2.1 6.4 5 7.7V22l3-3 3 3v-4.3c2.9-1.3 5-4.2 5-7.7a8 8 0 0 0-8-8z"
              />
            </svg>
            {{ panel.actions.find((a) => a.type === "chat_query").label }}
          </button>
          <span v-else class="text-xs text-gray-400">{{
            formatTime(panel.created_at)
          }}</span>
          <span v-if="panel.expires_at" class="text-xs text-gray-400">
            {{ $t("ai.dashboard.expiresIn") }}
            {{ expiresIn(panel.expires_at) }}
          </span>
          <span
            v-else-if="panel.actions?.some((a) => a.type === 'chat_query')"
            class="text-xs text-gray-400"
            >{{ formatTime(panel.created_at) }}</span
          >
        </div>
      </div>
    </div>

    <!-- Learning data notice -->
    <div
      v-if="meta && aiActivated && !meta.has_learning_data && !loading"
      class="mx-4 mt-4 flex items-center gap-2 px-4 py-2.5 bg-amber-50 border border-amber-200 rounded-lg"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="#d97706"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        class="flex-shrink-0"
      >
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="8" x2="12" y2="12" />
        <line x1="12" y1="16" x2="12.01" y2="16" />
      </svg>
      <p class="text-xs text-amber-700">
        {{ $t("ai.dashboard.noLearningData") }}
      </p>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { mapGetters } from "vuex";

export default {
  name: "AiDashboard",
  emits: ["chat-query", "navigate"],

  data() {
    return {
      panels: [],
      meta: null,
      aiActivated: true,
      loading: true,
      refreshing: false,
      draggedIndex: null,
      dragOverIndex: null,
    };
  },

  computed: {
    ...mapGetters(["hostName"]),
    headers() {
      return {
        Authorization: "Bearer " + localStorage.getItem("apiToken"),
      };
    },
    baseUrl() {
      return `${localStorage.getItem("apiUrl")}/api/v1.2/${
        this.hostName
      }/ai/dashboard`;
    },
  },

  methods: {
    async fetchDashboard() {
      if (!this.hostName) return;
      this.loading = true;
      try {
        const res = await axios.get(this.baseUrl, { headers: this.headers });
        console.log("res.data ==> ", res.data);
        this.applyResponse(res.data);
      } catch (e) {
        console.error("AiDashboard: fetch failed", e);
      } finally {
        this.loading = false;
      }
    },

    async refresh() {
      if (!this.hostName || this.refreshing) return;
      this.refreshing = true;
      try {
        const res = await axios.post(
          `${this.baseUrl}/refresh`,
          {},
          { headers: this.headers },
        );
        this.applyResponse(res.data);
      } catch (e) {
        console.error("AiDashboard: refresh failed", e);
      } finally {
        this.refreshing = false;
      }
    },

    async markAllRead() {
      try {
        await axios.post(
          `${this.baseUrl}/insights/read-all`,
          {},
          { headers: this.headers },
        );
        this.panels = this.panels.map((p) => ({ ...p, is_read: true }));
        if (this.meta) this.meta = { ...this.meta, unread_count: 0 };
      } catch (e) {
        console.error("AiDashboard: markAllRead failed", e);
      }
    },

    async markRead(panel) {
      if (panel.is_read) return;
      try {
        await axios.post(
          `${this.baseUrl}/insights/${panel.id}/read`,
          {},
          { headers: this.headers },
        );
        const idx = this.panels.findIndex((p) => p.id === panel.id);
        if (idx !== -1) {
          this.panels[idx] = { ...this.panels[idx], is_read: true };
          this.panels = [...this.panels];
        }
        if (this.meta && this.meta.unread_count > 0) {
          this.meta = {
            ...this.meta,
            unread_count: this.meta.unread_count - 1,
          };
        }
      } catch (e) {
        console.error("AiDashboard: markRead failed", e);
      }
    },

    async dismiss(panel) {
      try {
        await axios.post(
          `${this.baseUrl}/insights/${panel.id}/dismiss`,
          {},
          { headers: this.headers },
        );
        this.panels = this.panels.filter((p) => p.id !== panel.id);
        if (this.meta) {
          this.meta = {
            ...this.meta,
            total_panels: this.meta.total_panels - 1,
            unread_count: panel.is_read
              ? this.meta.unread_count
              : Math.max(0, this.meta.unread_count - 1),
          };
        }
      } catch (e) {
        console.error("AiDashboard: dismiss failed", e);
      }
    },

    handleAction(action, panel) {
      console.log("action, panel ==> ", action);
      this.markRead(panel);
      if (action.type === "chat_query") {
        this.$emit("chat-query", action.payload);
      } else if (action.type === "navigate") {
        this.$emit("navigate", { tab: action.payload });
      }
    },

    applyResponse(data) {
      const typeOrder = [
        "alert_summary",
        "kpi_snapshot",
        "trend",
        "pattern",
        "recommendation",
        "suggestion",
        "quick_action",
      ];
      if (data.meta?.ai_activated === false) {
        this.aiActivated = false;
        this.panels = [];
        this.meta = data.meta;
        return;
      }
      this.aiActivated = true;
      const rawPanels = data.panels || [];
      this.panels = [...rawPanels].sort((a, b) => {
        const ai = typeOrder.indexOf(a.type);
        const bi = typeOrder.indexOf(b.type);
        return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
      });
      this.meta = data.meta || null;
    },

    // ── Drag-and-drop ──

    onDragStart(index) {
      this.draggedIndex = index;
    },

    onDragOver(index) {
      this.dragOverIndex = index;
    },

    onDrop(index) {
      if (this.draggedIndex === null || this.draggedIndex === index) {
        this.draggedIndex = null;
        this.dragOverIndex = null;
        return;
      }
      const newPanels = [...this.panels];
      const [moved] = newPanels.splice(this.draggedIndex, 1);
      newPanels.splice(index, 0, moved);
      this.panels = newPanels;
      this.draggedIndex = null;
      this.dragOverIndex = null;
    },

    onDragEnd() {
      this.draggedIndex = null;
      this.dragOverIndex = null;
    },

    // ── Helpers ──

    panelColSpan(panel) {
      if (panel.type === "alert_summary" || panel.type === "quick_action") {
        return "lg:col-span-3";
      }
      if (panel.type === "recommendation") {
        return "lg:col-span-2";
      }
      return "lg:col-span-1";
    },

    barWidth(value, series) {
      const max = Math.max(...series.map((s) => s.value || 0), 1);
      return Math.round((value / max) * 100);
    },

    formatTime(ts) {
      if (!ts) return "";
      const d = new Date(ts);
      const now = new Date();
      const diff = (now - d) / 1000;
      if (diff < 60) return this.$t("ai.dashboard.justNow");
      if (diff < 3600) return `${Math.floor(diff / 60)}m`;
      if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
      return d.toLocaleDateString();
    },

    expiresIn(ts) {
      if (!ts) return "";
      const d = new Date(ts);
      const now = new Date();
      const diff = (d - now) / 1000;
      if (diff <= 0) return this.$t("ai.dashboard.expired");
      if (diff < 3600) return `${Math.floor(diff / 60)}m`;
      return `${Math.floor(diff / 3600)}h`;
    },

    // ── Style helpers ──

    severityBorderClass(severity) {
      if (severity === "critical")
        return "border-red-200 border-l-4 border-l-red-500";
      if (severity === "warning")
        return "border-yellow-200 border-l-4 border-l-yellow-400";
      if (severity === "info")
        return "border-blue-100 border-l-4 border-l-blue-400";
      return "border-gray-100";
    },

    severityCardClass(severity) {
      if (severity === "critical") return "border-red-200 bg-red-50/40";
      if (severity === "warning") return "border-yellow-200 bg-yellow-50/30";
      return "border-blue-100 bg-blue-50/20";
    },

    severityDotClass(severity) {
      if (severity === "critical") return "bg-red-500";
      if (severity === "warning") return "bg-yellow-400";
      return "bg-blue-400";
    },

    severityValueClass(severity) {
      if (severity === "critical") return "text-red-600";
      if (severity === "warning") return "text-yellow-500";
      return "text-blue-500";
    },

    severityBadgeClass(severity) {
      if (severity === "critical")
        return "bg-red-100 text-red-700 border border-red-200";
      if (severity === "warning")
        return "bg-yellow-100 text-yellow-700 border border-yellow-200";
      return "bg-blue-100 text-blue-700 border border-blue-200";
    },

    typeIconBgClass(type) {
      const map = {
        alert_summary: "bg-red-100 text-red-600",
        trend: "bg-teal-100 text-teal-600",
        kpi_snapshot: "bg-teal-100 text-teal-600",
        pattern: "bg-orange-100 text-orange-600",
        recommendation: "bg-blue-100 text-blue-600",
        suggestion: "bg-violet-100 text-violet-600",
        quick_action: "bg-purple-100 text-purple-600",
      };
      return map[type] || "bg-gray-100 text-gray-600";
    },

    typeBadgeClass(type) {
      const map = {
        alert_summary: "bg-red-50 text-red-600 border border-red-200",
        trend: "bg-teal-50 text-teal-700 border border-teal-200",
        kpi_snapshot: "bg-teal-50 text-teal-700 border border-teal-200",
        pattern: "bg-orange-50 text-orange-700 border border-orange-200",
        recommendation: "bg-blue-50 text-blue-700 border border-blue-200",
        suggestion: "bg-violet-50 text-violet-700 border border-violet-200",
        quick_action: "bg-purple-50 text-purple-700 border border-purple-200",
      };
      return map[type] || "bg-gray-100 text-gray-600 border border-gray-200";
    },
  },

  mounted() {
    this.fetchDashboard();
  },

  watch: {
    hostName(val) {
      if (val) this.fetchDashboard();
    },
  },
};
</script>
